import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface RoutingCondition {
  field: string;
  op: string;
  value: string; // comma-separated for "in"; ignored for "exists"
}

export interface RoutingRule {
  form_id?: string; // optional — scope to one form; empty = any form
  match: "all" | "any";
  conditions: RoutingCondition[];
  bundle_id: string;
}

// A form's question, normalized for the picker. `options` is non-empty for
// choice-type questions (select the answer); empty means free-text answer.
export interface RoutingQuestion {
  id: string;
  title: string;
  options: string[];
  // Question type from the form def (dropdown | yes_no | number | long_text |
  // short_text | checkboxes | email | phone | …). Drives the value input + ops.
  type?: string;
}

const OPS = ["eq", "in", "gt", "lt", "contains", "exists"] as const;

const OP_LABEL: Record<string, string> = {
  eq: "is",
  in: "is one of",
  contains: "contains",
  gt: ">",
  lt: "<",
  exists: "is answered",
};

// The operators that make sense for a question's answer type. Falls back to the
// full set for free-text fields / unknown types.
function opsForQuestion(q?: RoutingQuestion): { value: string; label: string }[] {
  const mk = (ops: string[]) =>
    ops.map((o) => ({ value: o, label: OP_LABEL[o] ?? o }));
  const t = q?.type;
  const hasOptions = !!q && q.options.length > 0;
  if (t === "yes_no") return mk(["eq"]);
  if (t === "number") return mk(["eq", "gt", "lt"]);
  if (t === "email" || t === "phone") return mk(["eq", "contains", "exists"]);
  if (t === "short_text" || t === "long_text" || t === "text")
    return mk(["contains", "eq", "exists"]);
  if (hasOptions) return mk(["eq", "in"]);
  return mk([...OPS]);
}

// Builder for answer → bundle routing rules. List order = priority (first match
// wins). Stored as the bundle/config JSON; see offerings-and-form-routing.md.
export default function OnboardingRoutingField({
  rules,
  onChange,
  forms,
  bundles,
  questionsByForm,
  lockedFormId,
}: {
  rules: RoutingRule[];
  onChange: (next: RoutingRule[]) => void;
  forms: { id: string; name: string }[];
  bundles: { id: string; name: string }[];
  // formId → that form's questions (for the question + answer pickers).
  questionsByForm: Record<string, RoutingQuestion[]>;
  // When set, every rule is scoped to this form: the per-rule Form dropdown is
  // hidden and new rules default their form_id to it. Used by the per-form
  // sub-editor so routing is authored under each form.
  lockedFormId?: string;
}) {
  const patchRule = (i: number, patch: Partial<RoutingRule>) =>
    onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRule = () =>
    onChange([
      ...rules,
      {
        match: "all",
        conditions: [{ field: "", op: "eq", value: "" }],
        bundle_id: "",
        ...(lockedFormId ? { form_id: lockedFormId } : {}),
      },
    ]);
  const removeRule = (i: number) => onChange(rules.filter((_, idx) => idx !== i));

  const patchCond = (ri: number, ci: number, patch: Partial<RoutingCondition>) =>
    patchRule(ri, {
      conditions: rules[ri].conditions.map((c, idx) =>
        idx === ci ? { ...c, ...patch } : c,
      ),
    });
  const addCond = (ri: number) =>
    patchRule(ri, {
      conditions: [...rules[ri].conditions, { field: "", op: "eq", value: "" }],
    });
  const removeCond = (ri: number, ci: number) =>
    patchRule(ri, {
      conditions: rules[ri].conditions.filter((_, idx) => idx !== ci),
    });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Routing rules{" "}
          <span className="text-gray-400">(top to bottom — first match wins)</span>
        </p>
        <Button type="button" size="sm" variant="outline" onClick={addRule}>
          <Plus className="h-4 w-4 mr-1" /> Add rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="text-xs text-gray-400">
          No rules — the default bundle is always used.
        </p>
      ) : (
        rules.map((rule, ri) => (
          <div
            key={ri}
            className="rounded-md border border-gray-200 p-3 space-y-3 bg-white"
          >
            <div className="flex items-center justify-between">
              <Badge variant="outline">Rule {ri + 1}</Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeRule(ri)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            <div className={lockedFormId ? "" : "grid grid-cols-2 gap-2"}>
              {!lockedFormId && (
                <div>
                  <p className="text-[11px] text-gray-500 mb-1">
                    Form (optional)
                  </p>
                  <Select
                    value={rule.form_id || "ANY"}
                    onValueChange={(v) =>
                      patchRule(ri, { form_id: v === "ANY" ? undefined : v })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Any form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ANY">Any form</SelectItem>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Match</p>
                <Select
                  value={rule.match}
                  onValueChange={(v) =>
                    patchRule(ri, { match: v as "all" | "any" })
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All conditions</SelectItem>
                    <SelectItem value="any">Any condition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] text-gray-500">Conditions</p>
              {rule.conditions.map((c, ci) => {
                const effectiveFormId = rule.form_id ?? lockedFormId;
                const formQuestions = effectiveFormId
                  ? (questionsByForm[effectiveFormId] ?? [])
                  : [];
                const selectedQ = formQuestions.find((q) => q.id === c.field);
                const isYesNo = selectedQ?.type === "yes_no";
                const isNumber = selectedQ?.type === "number";
                const showOptionSelect =
                  !isYesNo &&
                  !!selectedQ &&
                  selectedQ.options.length > 0 &&
                  c.op !== "in" &&
                  c.op !== "exists";
                // Operators valid for this question's type; keep the current op
                // listed even if not in the set (e.g. legacy saved value).
                const opChoices = opsForQuestion(selectedQ);
                const opList = opChoices.some((o) => o.value === c.op)
                  ? opChoices
                  : [{ value: c.op, label: OP_LABEL[c.op] ?? c.op }, ...opChoices];
                const labelCls =
                  "text-[10px] uppercase tracking-wide text-gray-400 mb-1";
                return (
                  <div
                    key={ci}
                    className="rounded-md border border-gray-100 bg-gray-50/60 p-2.5 space-y-2"
                  >
                    {/* Row 1 — question (full width, truncates on overflow). */}
                    <div className="min-w-0">
                      <p className={labelCls}>Question</p>
                      {formQuestions.length > 0 ? (
                        <Select
                          value={c.field || "NONE"}
                          onValueChange={(v) => {
                            const nextQ =
                              v === "NONE"
                                ? undefined
                                : formQuestions.find((q) => q.id === v);
                            patchCond(ri, ci, {
                              field: v === "NONE" ? "" : v,
                              value: "",
                              // Default the operator to one valid for the new
                              // question's type.
                              op: opsForQuestion(nextQ)[0]?.value ?? "eq",
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm w-full min-w-0 [&>span]:truncate">
                            <SelectValue placeholder="Select question" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">
                              Select question…
                            </SelectItem>
                            {formQuestions.map((q) => (
                              <SelectItem key={q.id} value={q.id}>
                                {q.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          className="h-8 text-sm w-full"
                          placeholder="answer field"
                          value={c.field}
                          onChange={(e) =>
                            patchCond(ri, ci, { field: e.target.value })
                          }
                        />
                      )}
                    </div>

                    {/* Row 2 — operator + answer + delete. */}
                    <div className="flex items-end gap-2">
                      <div className="w-28 shrink-0">
                        <p className={labelCls}>Operator</p>
                        <Select
                          value={c.op}
                          onValueChange={(v) => patchCond(ri, ci, { op: v })}
                        >
                          <SelectTrigger className="h-8 text-sm w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {opList.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={labelCls}>Answer</p>
                        {c.op === "exists" ? (
                          <Input
                            className="h-8 text-sm w-full"
                            disabled
                            placeholder="— not needed —"
                            value=""
                          />
                        ) : isYesNo ? (
                          <Select
                            value={c.value || "NONE"}
                            onValueChange={(v) =>
                              patchCond(ri, ci, {
                                value: v === "NONE" ? "" : v,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm w-full">
                              <SelectValue placeholder="Select Yes / No" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">Select…</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : showOptionSelect ? (
                          <Select
                            value={c.value || "NONE"}
                            onValueChange={(v) =>
                              patchCond(ri, ci, {
                                value: v === "NONE" ? "" : v,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm w-full min-w-0 [&>span]:truncate">
                              <SelectValue placeholder="Select answer" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">
                                Select answer…
                              </SelectItem>
                              {selectedQ!.options.map((o) => (
                                <SelectItem key={o} value={o}>
                                  {o}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : isNumber ? (
                          <Input
                            type="number"
                            className="h-8 text-sm w-full"
                            placeholder="value"
                            value={c.value}
                            onChange={(e) =>
                              patchCond(ri, ci, { value: e.target.value })
                            }
                          />
                        ) : (
                          <Input
                            className="h-8 text-sm w-full"
                            placeholder={c.op === "in" ? "a, b, c" : "value"}
                            value={c.value}
                            onChange={(e) =>
                              patchCond(ri, ci, { value: e.target.value })
                            }
                          />
                        )}
                      </div>

                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="shrink-0"
                        onClick={() => removeCond(ri, ci)}
                      >
                        <Trash2 className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => addCond(ri)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add condition
              </Button>
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">Direct to bundle</p>
              <Select
                value={rule.bundle_id || "NONE"}
                onValueChange={(v) =>
                  patchRule(ri, { bundle_id: v === "NONE" ? "" : v })
                }
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select bundle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Select bundle…</SelectItem>
                  {bundles.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
